/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Link } from 'react-router-dom';
import { string, shape, bool } from 'prop-types';
import { useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { trackEvent  } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { useDepartmentPageTracking, useIsDepartmentPageTrackingActive } from '../../../../helpers/tracking/page/DepartmentPageTrackingContext';

const useStyles = makeStyles(({ palette }) => ({
    menuNavFooter: {
        color: palette.black,
        borderTop: '1px solid #EBE1DB',
        padding: '16px 0px',
    },
    link: {
        display: 'block',
        padding: '3px 12px',
        color: palette.black,
        textDecoration: 'none',
        fontSize: '16px',
        lineHeight: '21px',
    },
    linkTitle: {
        fontSize: '1em',
        fontWeight: 'normal',
    },
    linkTitleBottom: {
        borderTop: '1px solid #EBE1DB',
        padding: '16px 0',
        width: '95%',
        '& $link': {
            padding: 0,
            fontSize: '18px',
            lineHeight: '23px',
        },
    },
    heading: {
        margin: '0 0 5px 10px',
    },
    parentLink: {
        padding: '0px 0px 4px',
        fontSize: '18px',
        lineHeight: '23px',
    },
    parentLinkContainer: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    lastTitle: {
        borderBottom: '1px solid #EBE1DB',
    },
}));

const LinkListContainer = ({
    data, uid,
}) => {
    const dispatch = useDispatch();
    const trackDepartmentPage = useDepartmentPageTracking();
    const isDepartmentPageTrackingActive = useIsDepartmentPageTrackingActive();
    const classes = useStyles();
    const [openSections, setOpenSections] = useState({});
    // const linkData = data;
    const linkData = data?.link_list?.reference?.[0] || data?.reference?.[0] || data;
    const links = linkData?.links || null;
    const heading = linkData?.heading || null;

    const trackFeatureClickEvent = (linking) => {
        if (isDepartmentPageTrackingActive) return;
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            eventSubType: 'feature-tracking',
            category: linking?.tracking_event_category || 'Browse',
            label: linking?.tracking_event_label || linking?.link?.title?.split(' ')?.join('-')?.toLowerCase() || '',
            link: linking?.link?.href || '',
            tag: linking?.tracking_event_action || '',
        }));
    };

    if (!linkData || !links) {
        return null;
    }

    const structuredLinks = () => {
        /* @intention: Convert a flat list of links into an object
         * with a heirarchy. Also duplicates parent links as child links
         * as to generate a clickable link for the parent category.
         * @return: {obj}
         */
        if (!links) return [];
        // clone links array to prevent error when navigating back
        const linkClone = JSON.parse(JSON.stringify(links));
        const linkStructure = [];
        let parentIndex = 0;
        linkClone.forEach((link, i) => {
            if (!link.link.hide) {
                if (!link.link.is_child) {
                    linkStructure[i] = [link];
                    linkStructure[i].child = [];
                    parentIndex = i;
                    linkStructure[i].child.push(link);
                } else {
                    linkStructure[parentIndex].child[0].link.is_child = true;
                    linkStructure[parentIndex].child.push(link);
                }
            }
        });
        return linkStructure;
    };
    const toggleSection = (id) => {
        setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
    };
    const getTabIndex = (k) => parseInt(k, 10) + 1;
    const entries = Object.entries(links || {});
    const visibleEntries = entries.filter(([, value]) => value?.link && value?.link?.hide !== true);
    const lastVisibleKey = visibleEntries[visibleEntries.length - 1]?.[0];
    return  (
        <div data-uniqueid={uid} className={classes.linkList}>
            {heading && <h3 className={classes.heading}>{heading}</h3>}
            {Object.entries(structuredLinks()).map(([key, value]) => {
                const toggleId = `toggle-${key}`;

                return !value[0].link?.is_child ? (
                    <Typography
                        className={`${classes.linkTitle} ${classes.linkTitleBottom} ${lastVisibleKey === key ? classes.lastTitle  : ''}`}
                        variant="h6"
                        key={toggleId}
                    >
                        <Link
                            className={classes.link}
                            title={value[0].link?.link?.title}
                            tabIndex={getTabIndex(key)}
                            to={value[0].link?.link?.href}
                            data-ga-category={value[0].link?.tracking_event_category || ''}
                            data-ga-action={value[0].link?.tracking_event_action || ''}
                            data-ga-label={value[0].link?.tracking_event_label || ''}
                            onClick={() => {
                                trackDepartmentPage(value[0].link?.link?.title || 'LinkList');
                                dispatch(
                                    trackEvent({
                                        eventCategory: value[0].link?.tracking_event_category || '',
                                        eventAction: value[0].link?.tracking_event_action || '',
                                        eventLabel: value[0].link?.tracking_event_label || '',
                                    }),
                                );
                                trackFeatureClickEvent(value[0].link);
                            }}
                        >
                            <b>{value[0].link?.link?.title}</b>
                        </Link>
                    </Typography>
                ) : (
                    <div className={classes.menuNavFooter} data-uniqueid={toggleId} key={toggleId}>
                        {value.child.length > 0 && (
                            <>
                                <div
                                    onClick={() => toggleSection(toggleId)}
                                    style={{ cursor: 'pointer' }}
                                    role="presentation"
                                    className={`${classes.parentLinkContainer} ${lastVisibleKey === key ? classes.lastTitle  : ''}`}
                                >
                                    <Typography  className={`${classes.link} ${classes.parentLink}`} variant="h6">
                                        <b>All {value.child[0].link?.link?.title}</b>
                                    </Typography>
                                    {openSections[toggleId] ? (
                                        <ChevronUp color="#2F363D" width={24} height={24} />
                                    ) : (
                                        <ChevronDown color="#2F363D" width={24} height={24} />
                                    )}
                                </div>

                                {openSections[toggleId] && (
                                    <div className={classes.subMenu}>
                                        {value.child.map((value2, key2) => (
                                            <Link
                                                // key={key2}
                                                className={classes.link}
                                                title={value2.link?.link?.title}
                                                to={value2.link?.link?.href}
                                                tabIndex={0}
                                                data-ga-category={value2.link?.tracking_event_category || ''}
                                                data-ga-action={value2.link?.tracking_event_action || ''}
                                                data-ga-label={value2.link?.tracking_event_label || ''}
                                                onClick={() => {
                                                    trackDepartmentPage(value2.link?.link?.title || 'LinkList');
                                                    dispatch(
                                                        trackEvent({
                                                            eventCategory: value2.link?.tracking_event_category || '',
                                                            eventAction: value2.link?.tracking_event_action || '',
                                                            eventLabel: value2.link?.tracking_event_label || '',
                                                        }),
                                                    );
                                                    trackFeatureClickEvent(value2.link);
                                                }}
                                            >
                                                {value2.link?.link?.title}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

LinkListContainer.propTypes = {
    data: shape({
        orientation: string.isRequired,
        presentation: string.isRequired,
        links: shape({
            link: shape({
                add_icon: bool.isRequired,
                is_child: bool.isRequired,
                link: shape({
                    href: string.isRequired,
                    title: string.isRequired,
                }).isRequired,
            }).isRequired,
        }).isRequired,
    }).isRequired,
    uid: string,
};
LinkListContainer.defaultProps = {
    uid: false,
};

export default LinkListContainer;
